import {useContext, useEffect, useMemo, useState} from 'react';

import {useField} from 'formik';
import {useTranslation} from 'react-i18next';

import {Autocomplete, Box, Grid, TextField, Typography, useTheme} from '@mui/material';

import EmpionAddableButton from '../EmpionAddableButton/EmpionAddableButton';
import {EmpionButton} from '../EmpionButton/EmpionButton';
import {EmpionLabeledValueList} from '../EmpionLabeledValueList/EmpionLabeledValueList';

import {LanguageContext} from '../../contexts/LanguageContext/LanguageContext';
import {StandardSkillsService} from '../../features/company/profile/services/StandardSkillsService';
import {StandardSkill} from '../../features/company/profile/types';
import {getFieldStyle} from '../../layouts/theme/FieldStyles';
import {SkillExperience} from '../../types/shared-types';

interface Props {
  name: string;
}

const isStandardSkill = (thing: any): thing is StandardSkill => !!thing?.resourceId;

export const SkillInput = ({name}: Props) => {
  const {t} = useTranslation();
  const {t: ts} = useTranslation('translation', {
    keyPrefix: 'shared.formMaker.skills',
  });
  const theme = useTheme();
  const currentLanguage = useContext(LanguageContext);

  const [suggestSkills, setSuggestSkills] = useState<string[]>(ts('suggestionList').split(','));
  const [skills, setSkills] = useState<SkillExperience[]>([]);
  const [standardSkills, setStandardSkills] = useState<StandardSkill[]>([]);
  const [standardSkill, setStandardSkill] = useState<StandardSkill>({
    resourceId: '',
    name: {en: '', de: ''},
  });

  const [field, , helpers] = useField(name);

  const standardSkillsService = useMemo(() => new StandardSkillsService(), []);

  const fetchStandardSkills = async () => {
    try {
      const response = await standardSkillsService.getStandardSkills(currentLanguage);

      setStandardSkills(response);
    } catch (error) {
      console.error('Failed to fetch standard skills:', error);
    }
  };

  useEffect(() => {
    const value = field.value as SkillExperience[];
    setSkills(value);
    fetchStandardSkills();
    // NOTE: This should only run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRemoveSkill = (skillId: string) => {
    setSkills(skills.filter((s) => s.id !== skillId));
  };

  const themedTextField = getFieldStyle(theme);
  const groups = useMemo(() => {
    const skillGroup = {
      groupName: ts('title'),
      color: 'secondary' as const,
      items: skills.map((skill) => ({
        id: skill.id,
        label: skill.name,
      })),
    };

    return [skillGroup];
  }, [ts, skills]);

  useEffect(() => {
    helpers.setValue(skills);
  }, [skills, helpers]);

  const addSkill = () => {
    let skillName = '';
    if (typeof standardSkill === 'string') {
      skillName = standardSkill;
    } else if (standardSkill && typeof standardSkill.name === 'object') {
      skillName = standardSkill.name[currentLanguage];
    }

    if (skillName !== '' && !skills.find((s) => s.name === skillName)) {
      setSkills([
        ...skills,
        {
          id: standardSkill.resourceId,
          name: standardSkill.name[currentLanguage],
          category: 'top_skill',
        },
      ]);
      setStandardSkill({
        resourceId: '',
        name: {en: '', de: ''},
      });
    }
  };

  return (
    <>
      <Typography variant='h2' gutterBottom>
        {t('shared.formMaker.skills.title')}
      </Typography>
      <Typography variant='body1' gutterBottom>
        {t('shared.formMaker.skills.description')}
      </Typography>
      <Typography variant='h3' my={2}>
        {t('shared.formMaker.skills.suggestions')}
      </Typography>
      <Grid container spacing={1} mb={2}>
        {suggestSkills.map((skill) => (
          <Grid item key={skill}>
            <EmpionAddableButton
              onAdd={() => {
                const skillToAdd = standardSkills.find((entry) => entry.name[currentLanguage] === skill);
                if (skillToAdd) {
                  setStandardSkill(skillToAdd);
                  setSuggestSkills((s) => s.filter((s) => s !== skill));
                }
              }}
              label={skill}
            />
          </Grid>
        ))}
      </Grid>
      <Autocomplete
        options={standardSkills}
        fullWidth
        freeSolo={true}
        data-testid='skill-input'
        value={standardSkill}
        getOptionLabel={(skill: any) => {
          if (typeof skill === 'string') {
            return skill;
          }
          return skill.name && typeof skill.name === 'object' ? skill.name[currentLanguage] : '';
        }}
        onChange={(_event, value: StandardSkill | string | null) => {
          if (value && isStandardSkill(value)) {
            setStandardSkill(value);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            name='standardCategory'
            sx={themedTextField}
            variant='outlined'
            size='small'
          />
        )}
      />
      <EmpionButton
        sx={{
          mt: 1,
        }}
        fullWidth
        onClick={addSkill}
        data-testid='add-skill-button'
      >
        {ts('addSkill')}
      </EmpionButton>
      <Box
        sx={{
          mt: 2,
          maxHeight: 320,
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: 8,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.primary.main,
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: theme.palette.primary.dark,
          },
        }}
      >
        <EmpionLabeledValueList light title={''} onDelete={(skill) => handleRemoveSkill(skill)} groups={groups} />
      </Box>
    </>
  );
};
